/** Live preview of how the user's row would appear to colleagues, rendered
 *  with the same look as the main Calendar grid. Driven by the *unsaved*
 *  visibility choice on the Settings page, so users see the effect of the
 *  privacy lever before clicking Save.
 *
 *  Server already returns the user's own absences at FULL via the self-
 *  shortcut, so we apply the visibility transform client-side here:
 *    - nothing  → row present with opt-out tint, no absence cells
 *    - binary   → cells coloured uniform (purple-light), no type in tooltip
 *    - full     → cells coloured by type, type in tooltip
 */
import { useMemo, useState } from "react";
import { useQuery } from "@tanstack/react-query";
import { useTranslation } from "react-i18next";

import { apiFetch } from "../api/client";
import type { AbsenceView, Visibility } from "../api/types";
import { holidayName } from "../lib/holidays";
import {
  WEEKDAY_EN,
  WEEKDAY_FR,
  colourForType,
  daysInMonth,
  dayCoverage,
  isoDate,
  isWeekend,
  sameDay,
  shiftMonth,
  todayYM,
  type YearMonth,
} from "../lib/calendar-utils";

export function VisibilityPreview({
  visibility,
  myOid,
  displayName,
}: {
  visibility: Visibility;
  myOid: string | undefined;
  displayName: string;
}) {
  const { t, i18n } = useTranslation();
  const [ym, setYm] = useState<YearMonth>(todayYM());
  const days = useMemo(() => daysInMonth(ym), [ym]);
  const start = isoDate(days[0]!);
  const end = isoDate(days[days.length - 1]!);
  const locale = i18n.language.startsWith("fr") ? "fr-FR" : "en-GB";
  const weekdays = i18n.language.startsWith("fr") ? WEEKDAY_FR : WEEKDAY_EN;
  const periodLabel = days[0]!.toLocaleDateString(locale, {
    month: "long",
    year: "numeric",
  });
  const today = new Date();

  const { data } = useQuery({
    queryKey: ["absences", start, end] as const,
    queryFn: () =>
      apiFetch<AbsenceView[]>(`/absences?start=${start}&end=${end}`),
    enabled: visibility !== "nothing",
  });

  // The server returns my own absences at FULL (self-shortcut). Filter to
  // just me, then apply the chosen visibility client-side so the preview
  // reflects the unsaved radio selection. In "nothing" mode the row still
  // renders (opt-out tint) but with no absence cells — see comment on the
  // useQuery `enabled` flag below.
  const myAbsences = useMemo(() => {
    if (visibility === "nothing" || !myOid) return [];
    const mine = (data ?? []).filter((a) => a.target_oid === myOid);
    if (visibility === "binary") {
      return mine.map((a) => ({ ...a, leave_type: null }));
    }
    return mine; // "full" — keep type as-is
  }, [data, myOid, visibility]);

  const types = useMemo(() => {
    const set = new Set<string>();
    for (const a of myAbsences) if (a.leave_type) set.add(a.leave_type);
    return Array.from(set).sort();
  }, [myAbsences]);

  const isNothing = visibility === "nothing";
  const optClass = isNothing ? " opt-out" : "";
  const nameTitle = isNothing
    ? `${displayName} — ${t("calendar.optOut")}`
    : displayName;
  const hintKey =
    isNothing
      ? "settings.previewNothing"
      : visibility === "binary"
        ? "settings.previewBinaryHint"
        : "settings.previewFullHint";

  return (
    <div className="vis-preview">
      <strong className="vis-preview-label">{t("settings.previewLabel")}</strong>

      <div className="calendar-controls vis-preview-controls">
        <button className="secondary" onClick={() => setYm(shiftMonth(ym, -1))}>
          ← {t("calendar.prev")}
        </button>
        <span className="period-label">{periodLabel}</span>
        <button className="secondary" onClick={() => setYm(shiftMonth(ym, 1))}>
          {t("calendar.next")} →
        </button>
        <button className="ghost" onClick={() => setYm(todayYM())}>
          {t("calendar.today")}
        </button>
      </div>

      <div className="calendar-wrap">
        <div
          className="calendar-grid"
          style={{ ["--day-count" as string]: days.length }}
        >
          <div className="name-head">{t("calendar.person")}</div>
          <div className="team-head">{t("calendar.team")}</div>
          {days.map((d) => {
            const isToday = sameDay(d, today);
            const hol = holidayName(d);
            const classes = [
              "day-head",
              isWeekend(d) ? "weekend" : "",
              hol ? "holiday" : "",
              isToday ? "today" : "",
            ]
              .filter(Boolean)
              .join(" ");
            const base = d.toLocaleDateString(locale, {
              weekday: "long",
              day: "numeric",
              month: "long",
            });
            return (
              <div
                key={`h-${d.getDate()}`}
                className={classes}
                title={hol ? `${base} — ${hol}` : base}
              >
                <div style={{ display: "flex", flexDirection: "column", lineHeight: 1.1 }}>
                  <span style={{ fontSize: "0.65rem", opacity: 0.7 }}>{weekdays[d.getDay()]}</span>
                  <span>{d.getDate()}</span>
                </div>
              </div>
            );
          })}

          <div className={`name-cell${optClass}`} title={nameTitle}>{displayName}</div>
          <div className={`team-cell${optClass}`}>—</div>
          {days.map((d) => {
            const isoD = isoDate(d);
            // Same rule as the real calendar: two half-days booked on one
            // date cover the whole day (cf. dayCoverage).
            const cov = dayCoverage(myAbsences, isoD);
            const colour = cov ? colourForType(cov.absences[0]!.leave_type) : "transparent";
            const cellAm = colour;
            const cellPm = cov && !cov.full ? "transparent" : colour;
            const hol = holidayName(d);
            const cls = [
              "day-cell",
              isWeekend(d) ? "weekend" : "",
              hol ? "holiday" : "",
              sameDay(d, today) ? "today" : "",
            ]
              .filter(Boolean)
              .join(" ");
            const datePart = d.toLocaleDateString(locale, { day: "numeric", month: "long" });
            const typePart = cov
              ? cov.types.map((ty) => ty ?? t("calendar.absentBinary")).join(" + ")
              : "";
            const halfPart = cov && !cov.full ? ` (${t("calendar.halfDay")})` : "";
            const title = cov
              ? `${displayName} — ${typePart}${halfPart} — ${datePart}${hol ? ` (${hol})` : ""}`
              : hol
                ? `${datePart} — ${hol}`
                : "";
            return (
              <div
                key={isoD}
                className={cls}
                style={cov ? { ["--cell-am" as string]: cellAm, ["--cell-pm" as string]: cellPm } : undefined}
                title={title}
              />
            );
          })}
        </div>
      </div>

      {(types.length > 0 || visibility === "binary" || isNothing) && (
        <div className="legend" aria-label={t("calendar.legend")}>
          {types.map((typ) => (
            <span key={typ} className="legend-item">
              <span className="legend-swatch" style={{ background: colourForType(typ) }} />
              {typ}
            </span>
          ))}
          {visibility === "binary" && (
            <span className="legend-item">
              <span className="legend-swatch" style={{ background: "var(--leave-binary)" }} />
              {t("calendar.absentBinary")}
            </span>
          )}
          {isNothing && (
            <span className="legend-item">
              <span className="legend-swatch" style={{ background: "var(--leave-optout)" }} />
              {t("calendar.optOut")}
            </span>
          )}
        </div>
      )}

      <p className="hint">{t(hintKey)}</p>
    </div>
  );
}
